<!-- src/apps/secret/reveal/UnknownSecret.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import BaseUnknownSecret from '@/shared/components/base/BaseUnknownSecret.vue';
  import NeedHelpModal from '@/shared/components/modals/NeedHelpModal.vue';
  import UnknownSecretHelpContent from '@/apps/secret/components/UnknownSecretHelpContent.vue';
  import type { BrandSettings } from '@/schemas/models/domain/brand';

const { t } = useI18n();

  interface Props {
    branded?: boolean;
    brandSettings?: BrandSettings;
  }

  defineProps<Props>();
</script>

<template>
  <BaseUnknownSecret
    :branded="branded"
    :brand-settings="brandSettings">
    <template #header="{}">
      <div class="mb-8 space-y-48">
        <!-- Header flex container with icon, title and help link -->
        <div class="mb-4 flex items-start justify-between">
          <div class="flex flex-col items-center">
            <router-link
              to="/"
              :aria-label="t('web.layout.return_to_home')">
              <svg
                class="mx-auto mb-4 size-20 text-brand-500"
                width="60"
                height="60"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-labelledby="secret-not-available-icon-title">
                <title id="secret-not-available-icon-title">{{ t("web.secrets.that_information_is_no_longer_available") }}</title>
                <path
                  d="M10 14L12 12M12 12L14 10M12 12L10 10M12 12L14 14M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </router-link>

            <h1 class="text-3xl font-bold text-gray-800 dark:text-white">
              {{ t("web.secrets.that_information_is_no_longer_available") }}
            </h1>
          </div>

          <!-- Help Modal Trigger -->
          <NeedHelpModal
            link-icon-name="question-mark-circle-16-solid"
            link-text-label=""
            aria-haspopup="dialog">
            <button
              type="button"
              class="ml-4 text-sm font-medium
                text-brand-600 hover:text-brand-500
                focus:underline focus:outline-none focus:ring-2 focus:ring-brand-500
                dark:text-brand-400 dark:hover:text-brand-300"
              :aria-label="t('web.COMMON.open_help_dialog')">
              {{ t('web.COMMON.need_help') }}
            </button>
            <template #content>
              <UnknownSecretHelpContent />
            </template>
          </NeedHelpModal>
        </div>
      </div>
    </template>

    <template #message="{}">
    </template>

    <template #alert="{}">
    </template>

    <template #action="{}">
      <!-- Canonical action buttons -->
      <div class="flex flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0">
        <router-link
          to="/"
          :class="[
            'inline-block rounded-lg border-2 transition duration-300 ease-in-out',
            'bg-white dark:bg-gray-800',
            'hover:bg-brand-100 dark:hover:bg-gray-700',
            'focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
            'focus:ring-brand-500 dark:focus:ring-brand-400',
            'px-6 py-3 font-brand text-lg hover:border-brand-600 dark:hover:border-brand-400',
            'w-full text-center sm:w-auto'
          ]"
          :style="{
            color: 'var(--color-brand-500)',
            borderColor: 'var(--color-brand-500)',
          }"
          :aria-label="t('web.layout.return_to_home_page')">
          {{ t('web.layout.return_to_home') }}
        </router-link>

        <router-link
          to="/"
          :class="[
            'inline-block rounded-lg transition duration-300 ease-in-out',
            'bg-brand-400 dark:bg-brand-600',
            'hover:bg-brand-100 dark:hover:bg-gray-700',
            'focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
            'focus:ring-brand-500 dark:focus:ring-brand-400',
            'px-6 py-3 font-brand text-lg hover:border-brand-600 dark:hover:border-brand-400',
            'w-full text-center sm:w-auto'
          ]"
          :aria-label="t('web.secrets.create_a_secret')">
          {{ t('web.secrets.create_a_secret') }}
        </router-link>
      </div>
    </template>
  </BaseUnknownSecret>
</template>
